import type { SettingsItem } from '@/components/organisms';

export const mockSettings: SettingsItem[] = [
  // General Settings
  {
    id: 'auto-save',
    key: 'files.autoSave',
    label: 'Auto Save',
    description: 'Controls auto save of dirty editors.',
    type: 'select',
    value: 'afterDelay',
    defaultValue: 'off',
    options: [
      { label: 'Off', value: 'off', description: 'Disable auto save' },
      { label: 'After Delay', value: 'afterDelay', description: 'Save after a delay' },
      { label: 'On Focus Change', value: 'onFocusChange', description: 'Save when focus moves away' },
      { label: 'On Window Change', value: 'onWindowChange', description: 'Save when window loses focus' }
    ],
    category: 'general',
    subcategory: 'files',
    tags: ['autosave', 'files'],
    scope: 'both'
  },
  {
    id: 'auto-save-delay',
    key: 'files.autoSaveDelay',
    label: 'Auto Save Delay',
    description: 'Controls the delay in ms after which a dirty editor is saved automatically.',
    type: 'number',
    value: 1000,
    defaultValue: 1000,
    category: 'general',
    subcategory: 'files',
    tags: ['autosave', 'delay'],
    scope: 'both'
  },
  {
    id: 'confirm-delete',
    key: 'explorer.confirmDelete',
    label: 'Confirm Delete',
    description: 'Controls whether the explorer should ask for confirmation when deleting a file via the trash.',
    type: 'boolean',
    value: true,
    defaultValue: true,
    category: 'general',
    subcategory: 'explorer',
    tags: ['delete', 'confirmation'],
    scope: 'user'
  },
  
  // Editor Settings
  {
    id: 'font-size',
    key: 'editor.fontSize',
    label: 'Font Size',
    description: 'Controls the font size in pixels.',
    type: 'number',
    value: 14,
    defaultValue: 14,
    category: 'editor',
    tags: ['font', 'size'],
    scope: 'both'
  },
  {
    id: 'font-family',
    key: 'editor.fontFamily',
    label: 'Font Family',
    description: 'Controls the font family.',
    type: 'string',
    value: 'Monaco, Menlo, "Ubuntu Mono", monospace',
    defaultValue: 'Monaco, Menlo, "Ubuntu Mono", monospace',
    category: 'editor',
    tags: ['font', 'family'],
    scope: 'both'
  },
  {
    id: 'word-wrap',
    key: 'editor.wordWrap',
    label: 'Word Wrap',
    description: 'Controls how lines should wrap.',
    type: 'select',
    value: 'off',
    defaultValue: 'off',
    options: [
      { label: 'Off', value: 'off', description: 'Lines will never wrap' },
      { label: 'On', value: 'on', description: 'Lines will wrap at the viewport width' },
      { label: 'Word Wrap Column', value: 'wordWrapColumn', description: 'Lines will wrap at wordWrapColumn' },
      { label: 'Bounded', value: 'bounded', description: 'Lines will wrap at the minimum of viewport and wordWrapColumn' }
    ],
    category: 'editor',
    tags: ['wrap', 'text'],
    scope: 'both'
  },
  {
    id: 'tab-size',
    key: 'editor.tabSize',
    label: 'Tab Size',
    description: 'The number of spaces a tab is equal to.',
    type: 'number',
    value: 2,
    defaultValue: 4,
    category: 'editor',
    subcategory: 'indentation',
    tags: ['tab', 'indentation'],
    scope: 'both'
  },
  {
    id: 'insert-spaces',
    key: 'editor.insertSpaces',
    label: 'Insert Spaces',
    description: 'Insert spaces when pressing Tab.',
    type: 'boolean',
    value: true,
    defaultValue: true,
    category: 'editor',
    subcategory: 'indentation',
    tags: ['spaces', 'tab', 'indentation'],
    scope: 'both'
  },
  {
    id: 'line-numbers',
    key: 'editor.lineNumbers',
    label: 'Line Numbers',
    description: 'Controls the display of line numbers.',
    type: 'select',
    value: 'on',
    defaultValue: 'on',
    options: [
      { label: 'Off', value: 'off', description: 'No line numbers' },
      { label: 'On', value: 'on', description: 'Show line numbers' },
      { label: 'Relative', value: 'relative', description: 'Show relative line numbers' },
      { label: 'Interval', value: 'interval', description: 'Show line numbers every 10 lines' }
    ],
    category: 'editor',
    tags: ['line', 'numbers'],
    scope: 'both'
  },
  {
    id: 'minimap-enabled',
    key: 'editor.minimap.enabled',
    label: 'Minimap',
    description: 'Controls whether the minimap is shown.',
    type: 'boolean',
    value: true,
    defaultValue: true,
    category: 'editor',
    subcategory: 'minimap',
    tags: ['minimap'],
    scope: 'both'
  },
  
  // Workbench Settings
  {
    id: 'color-theme',
    key: 'workbench.colorTheme',
    label: 'Color Theme',
    description: 'Specifies the color theme used in the workbench.',
    type: 'select',
    value: 'Default Dark+',
    defaultValue: 'Default Light+',
    options: [
      { label: 'Default Light+', value: 'Default Light+' },
      { label: 'Default Dark+', value: 'Default Dark+' },
      { label: 'Default High Contrast', value: 'Default High Contrast' },
      { label: 'Monokai', value: 'Monokai' },
      { label: 'Solarized Dark', value: 'Solarized Dark' },
      { label: 'Solarized Light', value: 'Solarized Light' }
    ],
    category: 'workbench',
    subcategory: 'appearance',
    tags: ['theme', 'color'],
    scope: 'user'
  },
  {
    id: 'sidebar-location',
    key: 'workbench.sideBar.location',
    label: 'Side Bar Location',
    description: 'Controls the location of the side bar.',
    type: 'select',
    value: 'left',
    defaultValue: 'left',
    options: [
      { label: 'Left', value: 'left' },
      { label: 'Right', value: 'right' }
    ],
    category: 'workbench',
    subcategory: 'layout',
    tags: ['sidebar', 'layout'],
    scope: 'user'
  },
  {
    id: 'activity-bar-visible',
    key: 'workbench.activityBar.visible',
    label: 'Activity Bar Visible',
    description: 'Controls the visibility of the activity bar in the workbench.',
    type: 'boolean',
    value: true,
    defaultValue: true,
    category: 'workbench',
    subcategory: 'layout',
    tags: ['activity', 'bar', 'layout'],
    scope: 'user'
  },
  
  // Security Settings
  {
    id: 'workspace-trust',
    key: 'security.workspace.trust.enabled',
    label: 'Workspace Trust',
    description: 'Controls whether or not workspace trust is enabled within VS Code.',
    type: 'boolean',
    value: true,
    defaultValue: true,
    category: 'security',
    tags: ['trust', 'workspace'],
    scope: 'user',
    experimental: true
  },
  {
    id: 'untrusted-files',
    key: 'security.workspace.trust.untrustedFiles',
    label: 'Untrusted Files',
    description: 'Controls how to handle loose files in a workspace.',
    type: 'select',
    value: 'prompt',
    defaultValue: 'prompt',
    options: [
      { label: 'Prompt', value: 'prompt', description: 'Ask how to handle untrusted files' },
      { label: 'Open', value: 'open', description: 'Always open untrusted files' },
      { label: 'New Window', value: 'newWindow', description: 'Always open in new window' }
    ],
    category: 'security',
    tags: ['trust', 'files'],
    scope: 'user'
  },
  
  // Extensions Settings
  {
    id: 'auto-check-updates',
    key: 'extensions.autoCheckUpdates',
    label: 'Auto Check Updates',
    description: 'Controls whether to automatically check for extension updates.',
    type: 'boolean',
    value: true,
    defaultValue: true,
    category: 'extensions',
    tags: ['updates', 'automatic'],
    scope: 'user'
  },
  {
    id: 'auto-update',
    key: 'extensions.autoUpdate',
    label: 'Auto Update',
    description: 'Controls whether to automatically update extensions.',
    type: 'boolean',
    value: true,
    defaultValue: true,
    category: 'extensions',
    tags: ['updates', 'automatic'],
    scope: 'user'
  },
  {
    id: 'ignore-recommendations',
    key: 'extensions.ignoreRecommendations',
    label: 'Ignore Recommendations',
    description: 'Controls whether to ignore extension recommendations.',
    type: 'boolean',
    value: false,
    defaultValue: false,
    category: 'extensions',
    tags: ['recommendations'],
    scope: 'user'
  },
  
  // Deprecated Settings
  {
    id: 'legacy-setting',
    key: 'editor.legacy.feature',
    label: 'Legacy Feature',
    description: 'This setting has been deprecated and will be removed in a future version.',
    type: 'boolean',
    value: false,
    defaultValue: false,
    category: 'editor',
    tags: ['legacy'],
    scope: 'both',
    deprecated: true
  }
];
